import { z } from 'zod';
import { AlertTypeSchema, ExecutionStatusSchema, IntervalUnitSchema, RecurringRuleStatusSchema, RecurringRuleTriggerTypeSchema, RestrictionModeSchema, RestrictionTypeSchema, ThresholdTypeSchema, TopUpMethodSchema, WalletStatusSchema, WalletTransactionStatusSchema, WalletTransactionTypeSchema, } from '../common/enums.js';
import { MetadataSchema, PaginationQuerySchema } from '../common/types.js';
export const WalletSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    customer_id: z.string(),
    organization_id: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    currency: z.string(),
    status: WalletStatusSchema,
    balance: z.number(),
    credits_balance: z.number(),
    ongoing_balance: z.number(),
    ongoing_usage_balance: z.number(),
    conversion_rate: z.number(),
    auto_recharge_enabled: z.boolean(),
    auto_recharge_threshold: z.number().nullable(),
    auto_recharge_amount: z.number().nullable(),
    auto_recharge_max_amount: z.number().nullable(),
    allow_negative_balance: z.boolean(),
    invoice_requires_payment: z.boolean(),
    low_balance_alert_threshold: z.number().nullable(),
    last_balance_sync_at: z.string().datetime().nullable(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    created_by: z.string().nullable(),
    updated_by: z.string().nullable(),
    lock_version: z.number().int(),
});
export const WalletTransactionSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    wallet_id: z.string(),
    organization_id: z.string().nullable(),
    type: WalletTransactionTypeSchema,
    transaction_type: WalletTransactionTypeSchema,
    status: WalletTransactionStatusSchema,
    amount: z.number(),
    currency: z.string(),
    credit_amount: z.number().nullable(),
    conversion_rate_snapshot: z.number().nullable(),
    credit_type: z.string().nullable(),
    balance_before: z.number().nullable(),
    balance_after: z.number().nullable(),
    credits_before: z.number().nullable(),
    credits_after: z.number().nullable(),
    credits_remaining: z.number().nullable(),
    source: z.string().nullable(),
    source_id: z.string().nullable(),
    invoice_id: z.string().nullable(),
    payment_id: z.string().nullable(),
    credit_note_id: z.string().nullable(),
    description: z.string().nullable(),
    notes: z.string().nullable(),
    reference_id: z.string().optional(),
    reference_type: z.string().optional(),
    expiry_date: z.string().datetime().nullable(),
    expired_at: z.string().datetime().nullable(),
    processed_at: z.string().datetime().nullable(),
    failed_at: z.string().datetime().nullable(),
    failure_reason: z.string().nullable(),
    retry_count: z.number().int().default(0),
    idempotency_key: z.string().nullable(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    created_by: z.string().nullable(),
    lock_version: z.number().int(),
});
export const RecurringTransactionRuleSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    wallet_id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    status: RecurringRuleStatusSchema,
    trigger_type: RecurringRuleTriggerTypeSchema,
    interval_unit: IntervalUnitSchema.nullable().optional(),
    interval_value: z.number().nullable().optional(),
    time_of_day: z.string().nullable().optional(),
    start_date: z.string().datetime().nullable().optional(),
    timezone: z.string().nullable().optional(),
    threshold_amount: z.number().nullable(),
    threshold_type: ThresholdTypeSchema.nullable().optional(),
    threshold_value: z.number().nullable().optional(),
    top_up_method: TopUpMethodSchema,
    fixed_top_up_amount: z.number().nullable(),
    target_balance_amount: z.number().nullable(),
    max_top_up_amount: z.number().nullable(),
    last_execution_at: z.string().datetime().nullable(),
    next_execution_at: z.string().datetime().nullable(),
    execution_count: z.number().nullable().optional(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const RecurringTransactionExecutionSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    rule_id: z.string(),
    wallet_id: z.string(),
    execution_status: ExecutionStatusSchema,
    amount_processed: z.number().nullable(),
    error_message: z.string().nullable(),
    transaction_id: z.string().nullable(),
    triggered_at: z.string().datetime(),
    completed_at: z.string().datetime().nullable(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
});
export const UsageRestrictionSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    wallet_id: z.string(),
    restriction_name: z.string(),
    description: z.string().nullable(),
    restriction_type: z.string(),
    max_transaction_amount: z.number().nullable(),
    daily_spending_limit: z.number().nullable(),
    monthly_spending_limit: z.number().nullable(),
    allowed_transaction_types: z.array(z.string()).nullable(),
    is_active: z.boolean(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const WalletAlertSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    wallet_id: z.string(),
    alert_type: AlertTypeSchema,
    threshold_amount: z.number().nullable(),
    notification_channels: z.array(z.string()),
    is_active: z.boolean(),
    last_triggered_at: z.string().datetime().nullable(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateWalletSchema = z.object({
    customer_id: z.string(),
    organization_id: z.string().optional(),
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    currency: z.string().default('USD'),
    status: WalletStatusSchema.default('active'),
    conversion_rate: z.number().positive().default(1.0).optional(),
    initial_credits: z.number().min(0).optional(),
    auto_recharge_enabled: z.boolean().default(false),
    auto_recharge_threshold: z.number().min(0).optional(),
    auto_recharge_amount: z.number().positive().optional(),
    auto_recharge_max_amount: z.number().positive().optional(),
    allow_negative_balance: z.boolean().default(false),
    invoice_requires_payment: z.boolean().default(true),
    low_balance_alert_threshold: z.number().min(0).optional(),
    metadata: MetadataSchema.default({}),
});
export const CreateWalletTransactionSchema = z.object({
    transaction_type: WalletTransactionTypeSchema,
    type: WalletTransactionTypeSchema.optional(),
    amount: z.number().positive(),
    credit_amount: z.number().positive().optional(),
    credit_type: z.string().optional(),
    source: z.string().optional(),
    source_id: z.string().optional(),
    invoice_id: z.string().optional(),
    payment_id: z.string().optional(),
    credit_note_id: z.string().optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
    reference_id: z.string().optional(),
    reference_type: z.string().optional(),
    expiry_date: z.string().datetime().optional(),
    idempotency_key: z.string().optional(),
    metadata: MetadataSchema.default({}),
});
export const CreateRecurringRuleSchema = z.object({
    wallet_id: z.string(),
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    status: RecurringRuleStatusSchema.default('active'),
    trigger_type: RecurringRuleTriggerTypeSchema,
    interval_unit: IntervalUnitSchema.optional(),
    interval_value: z.number().int().positive().optional(),
    day_of_week: z.number().int().min(0).max(6).optional(),
    day_of_month: z.number().int().min(1).max(31).optional(),
    time_of_day: z.string().optional(),
    timezone: z.string().default('UTC'),
    threshold_type: ThresholdTypeSchema.optional(),
    threshold_value: z.number().min(0).optional(),
    top_up_method: TopUpMethodSchema,
    fixed_amount: z.number().positive().optional(),
    target_balance: z.number().positive().optional(),
    max_amount_per_transaction: z.number().positive().optional(),
    start_date: z.string().datetime().optional(),
    end_date: z.string().datetime().optional(),
    max_executions: z.number().int().positive().optional(),
    payment_method_id: z.string().optional(),
    require_payment_confirmation: z.boolean().default(false),
    notify_on_execution: z.boolean().default(false),
    notify_on_failure: z.boolean().default(true),
    notification_emails: z.array(z.string().email()).default([]),
    metadata: MetadataSchema.default({}),
});
export const CreateUsageRestrictionSchema = z.object({
    wallet_id: z.string(),
    restriction_type: RestrictionTypeSchema,
    restriction_mode: RestrictionModeSchema,
    allowed_fee_types: z.array(z.string()).default([]),
    agent_ids: z.array(z.string()).default([]),
    signal_ids: z.array(z.string()).default([]),
    feature_ids: z.array(z.string()).default([]),
    min_transaction_amount: z.number().positive().optional(),
    max_transaction_amount: z.number().positive().optional(),
    daily_limit: z.number().positive().optional(),
    monthly_limit: z.number().positive().optional(),
    valid_from: z.string().datetime().optional(),
    valid_until: z.string().datetime().optional(),
    allowed_hours: z
        .array(z.object({
        start: z.string(),
        end: z.string(),
    }))
        .default([]),
    allowed_days: z.array(z.number().int().min(0).max(6)).default([]),
    description: z.string().optional(),
    metadata: MetadataSchema.default({}),
});
export const CreateWalletAlertSchema = z.object({
    alert_type: AlertTypeSchema,
    threshold_value: z.number().min(0).optional(),
    threshold_percentage: z.number().min(0).max(100).optional(),
    enabled: z.boolean().default(true),
    cooldown_minutes: z.number().int().positive().default(60),
    max_triggers_per_day: z.number().int().positive().default(5),
    notification_channels: z.array(z.string()).min(1).default(['email']),
    notification_emails: z.array(z.string().email()).default([]),
    webhook_url: z.string().url().optional(),
    custom_message: z.string().optional(),
    include_balance_info: z.boolean().default(true),
    include_usage_info: z.boolean().default(false),
    metadata: MetadataSchema.default({}),
});
export const UpdateWalletSchema = CreateWalletSchema.partial().omit({
    customer_id: true,
});
export const UpdateRecurringRuleSchema = CreateRecurringRuleSchema.partial().omit({ wallet_id: true });
export const UpdateUsageRestrictionSchema = CreateUsageRestrictionSchema.partial().omit({ wallet_id: true });
export const UpdateWalletAlertSchema = CreateWalletAlertSchema.partial();
export const WalletQuerySchema = PaginationQuerySchema.extend({
    customer_id: z.string().optional(),
    organization_id: z.string().optional(),
    status: WalletStatusSchema.optional(),
    currency: z.string().optional(),
    search: z.string().optional(),
});
export const TransactionQuerySchema = PaginationQuerySchema.extend({
    transaction_type: WalletTransactionTypeSchema.optional(),
    status: WalletTransactionStatusSchema.optional(),
    source: z.string().optional(),
    from_date: z.string().datetime().optional(),
    to_date: z.string().datetime().optional(),
});
export const WalletResponseSchema = WalletSchema.extend({
    transactions: z.array(WalletTransactionSchema).optional(),
    recurring_rules: z.array(RecurringTransactionRuleSchema).optional(),
    restrictions: z.array(UsageRestrictionSchema).optional(),
    alerts: z.array(WalletAlertSchema).optional(),
});
export const PaginatedWalletsResponseSchema = z.object({
    data: z.array(WalletResponseSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
        has_next: z.boolean(),
        has_prev: z.boolean(),
    }),
});
export const PaginatedTransactionsResponseSchema = z.object({
    data: z.array(WalletTransactionSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
        has_next: z.boolean(),
        has_prev: z.boolean(),
    }),
});
export const RecurringRulesArraySchema = z.array(RecurringTransactionRuleSchema);
export const UsageRestrictionsArraySchema = z.array(UsageRestrictionSchema);
export const WalletAlertsArraySchema = z.array(WalletAlertSchema);
export const WalletParamsSchema = z.object({
    walletId: z.string(),
});
export const CustomerWalletParamsSchema = z.object({
    customerId: z.string(),
});
export const TransactionParamsSchema = z.object({
    walletId: z.string(),
    transactionId: z.string(),
});
export const RecurringRuleParamsSchema = z.object({
    walletId: z.string(),
    ruleId: z.string(),
});
export const UsageRestrictionParamsSchema = z.object({
    walletId: z.string(),
    restrictionId: z.string(),
});
export const WalletAlertParamsSchema = z.object({
    walletId: z.string(),
    alertId: z.string(),
});
export const RechargeWalletSchema = z.object({
    amount: z.number().positive(),
    description: z.string().optional(),
});
export const WalletBalanceResponseSchema = z.object({
    wallet_id: z.string(),
    balance: z.number(),
    credits_balance: z.number(),
    ongoing_balance: z.number(),
    ongoing_usage_balance: z.number(),
    currency: z.string(),
    last_updated: z.string().datetime(),
});
export const ValidateUsageSchema = z.object({
    amount: z.number().positive(),
    agent_id: z.string().optional(),
    signal_id: z.string().optional(),
    feature_id: z.string().optional(),
    fee_type: z.enum(['platform', 'seat', 'usage', 'setup']).optional(),
});
export const ValidateUsageResponseSchema = z.object({
    allowed: z.boolean(),
    reason: z.string().optional(),
});
//# sourceMappingURL=index.js.map